import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { 
  ClipboardCheck, Wine, Pill, Dices, Brain, Calculator, 
  ArrowLeft, ArrowRight, CheckCircle, AlertTriangle, 
  AlertCircle, Phone, Building2, BookOpen, Users, Heart, Sparkles,
  Calendar, MessageSquare, Eye, Mirror, UserCircle, Flame, Zap, Trophy
} from 'lucide-react';
import NotFoundPage from './NotFoundPage';

// ==================== TEST DATA ====================

const TESTS = {
  alcoholismo: {
    id: 'alcoholismo',
    slug: 'test-alcoholismo',
    title: 'Test AUDIT-10: Nivel de Riesgo por Consumo de Alcohol',
    shortTitle: 'Test Alcoholismo',
    description: 'Mide tu riesgo por alcohol en los últimos 12 meses (OMS/SENDA). 10 preguntas → resultado: bajo, moderado, alto o posible dependencia.',
    icon: Wine,
    color: 'amber',
    duration: '3 minutos',
    questions: [
      {
        id: 1,
        text: '¿Con qué frecuencia consume bebidas alcohólicas?',
        options: [
          { value: 0, label: 'Nunca' },
          { value: 1, label: 'Una vez al mes o menos' },
          { value: 2, label: '2 a 4 veces al mes' },
          { value: 3, label: '2 a 3 veces por semana' },
          { value: 4, label: '4 o más veces por semana' }
        ]
      },
      {
        id: 2,
        text: '¿Cuántas bebidas alcohólicas consume en un día típico en que bebe?',
        options: [
          { value: 0, label: '1 o 2' },
          { value: 1, label: '3 o 4' },
          { value: 2, label: '5 o 6' },
          { value: 3, label: '7 a 9' },
          { value: 4, label: '10 o más' }
        ]
      },
      {
        id: 3,
        text: '¿Con qué frecuencia consume 6 o más bebidas en una sola ocasión?',
        options: [
          { value: 0, label: 'Nunca' },
          { value: 1, label: 'Menos de una vez al mes' },
          { value: 2, label: 'Mensualmente' },
          { value: 3, label: 'Semanalmente' },
          { value: 4, label: 'Diariamente o casi' }
        ]
      },
      {
        id: 4,
        text: '¿Con qué frecuencia, en el último año, ha descubierto que no podía parar de beber una vez que había empezado?',
        options: [
          { value: 0, label: 'Nunca' },
          { value: 1, label: 'Menos de una vez al mes' },
          { value: 2, label: 'Mensualmente' },
          { value: 3, label: 'Semanalmente' },
          { value: 4, label: 'Diariamente o casi' }
        ]
      },
      {
        id: 5,
        text: '¿Con qué frecuencia, en el último año, ha fallado en hacer lo que se esperaba de usted debido al consumo de alcohol?',
        options: [
          { value: 0, label: 'Nunca' },
          { value: 1, label: 'Menos de una vez al mes' },
          { value: 2, label: 'Mensualmente' },
          { value: 3, label: 'Semanalmente' },
          { value: 4, label: 'Diariamente o casi' }
        ]
      },
      {
        id: 6,
        text: '¿Con qué frecuencia, en el último año, ha necesitado beber por la mañana para ponerse en marcha después de una noche de mucho consumo?',
        options: [
          { value: 0, label: 'Nunca' },
          { value: 1, label: 'Menos de una vez al mes' },
          { value: 2, label: 'Mensualmente' },
          { value: 3, label: 'Semanalmente' },
          { value: 4, label: 'Diariamente o casi' }
        ]
      },
      {
        id: 7,
        text: '¿Con qué frecuencia, en el último año, ha tenido sentimientos de culpa o remordimiento después de beber?',
        options: [
          { value: 0, label: 'Nunca' },
          { value: 1, label: 'Menos de una vez al mes' },
          { value: 2, label: 'Mensualmente' },
          { value: 3, label: 'Semanalmente' },
          { value: 4, label: 'Diariamente o casi' }
        ]
      },
      {
        id: 8,
        text: '¿Con qué frecuencia, en el último año, ha sido incapaz de recordar lo que pasó la noche anterior debido al consumo de alcohol?',
        options: [
          { value: 0, label: 'Nunca' },
          { value: 1, label: 'Menos de una vez al mes' },
          { value: 2, label: 'Mensualmente' },
          { value: 3, label: 'Semanalmente' },
          { value: 4, label: 'Diariamente o casi' }
        ]
      },
      {
        id: 9,
        text: '¿Ha sufrido usted o alguien más alguna lesión debido a su consumo de alcohol?',
        options: [
          { value: 0, label: 'No' },
          { value: 2, label: 'Sí, pero no en el último año' },
          { value: 4, label: 'Sí, en el último año' }
        ]
      },
      {
        id: 10,
        text: '¿Algún familiar, amigo, médico o profesional de salud ha estado preocupado por su consumo de alcohol o le ha sugerido que lo reduzca?',
        options: [
          { value: 0, label: 'No' },
          { value: 2, label: 'Sí, pero no en el último año' },
          { value: 4, label: 'Sí, en el último año' }
        ]
      }
    ],
    getResult: (score) => {
      if (score <= 7) return { level: 'bajo', title: 'Riesgo Bajo', color: 'emerald', description: 'Tu consumo de alcohol parece estar dentro de límites de bajo riesgo. Mantén estos hábitos saludables.' };
      if (score <= 15) return { level: 'moderado', title: 'Riesgo Moderado', color: 'yellow', description: 'Tu patrón de consumo indica un riesgo moderado. Considera reducir la cantidad y frecuencia de consumo.' };
      if (score <= 19) return { level: 'alto', title: 'Riesgo Alto', color: 'orange', description: 'Tu consumo de alcohol es preocupante. Se recomienda buscar orientación profesional.' };
      return { level: 'severo', title: 'Posible Dependencia', color: 'red', description: 'Los resultados sugieren una posible dependencia al alcohol. Es importante buscar ayuda profesional lo antes posible.' };
    }
  },
  dependencia: {
    id: 'dependencia',
    slug: 'test-dependencia',
    title: 'Test DAST-10: Nivel de Dependencia a Sustancias',
    shortTitle: 'Test Dependencia',
    description: 'Evalúa en 3 minutos tu riesgo por consumo de drogas (marihuana, cocaína, pasta base, etc.) en los últimos 12 meses.',
    icon: Pill,
    color: 'purple',
    duration: '3 minutos',
    questions: [
      { id: 1, text: '¿Ha usado drogas que no eran requeridas por razones médicas?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
      { id: 2, text: '¿Usted abusa de más de una droga a la vez?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
      { id: 3, text: '¿Es usted capaz de parar de usar drogas siempre cuando se lo propone?', options: [{ value: 0, label: 'Sí' }, { value: 1, label: 'No' }] },
      { id: 4, text: '¿Ha tenido «pérdidas de conocimiento» o «memoria repentina» como resultado del uso de drogas?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
      { id: 5, text: '¿Alguna vez se ha sentido mal o culpable debido a su uso de drogas?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
      { id: 6, text: '¿Alguna vez su pareja o familiares se han quejado de su uso de drogas?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
      { id: 7, text: '¿Ha desatendido a su familia debido a su uso de drogas?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
      { id: 8, text: '¿Se ha implicado en actividades ilegales para obtener drogas?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
      { id: 9, text: '¿Alguna vez ha experimentado síntomas de abstinencia (sentirse enfermo) al dejar las drogas?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
      { id: 10, text: '¿Ha tenido problemas médicos por uso de drogas (pérdida memoria, hepatitis, etc.)?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] }
    ],
    getResult: (score) => {
      if (score === 0) return { level: 'ninguno', title: 'Sin Problema Detectado', color: 'emerald', description: 'No se detectan problemas relacionados con el uso de sustancias.' };
      if (score <= 2) return { level: 'bajo', title: 'Nivel Bajo', color: 'yellow', description: 'Existe un nivel bajo de problemas relacionados con drogas. Monitorea tu consumo.' };
      if (score <= 5) return { level: 'moderado', title: 'Nivel Moderado', color: 'orange', description: 'Nivel moderado de problemas. Se recomienda buscar orientación profesional.' };
      if (score <= 8) return { level: 'sustancial', title: 'Nivel Sustancial', color: 'red', description: 'Nivel sustancial de problemas. Es importante buscar tratamiento.' };
      return { level: 'severo', title: 'Nivel Severo', color: 'red', description: 'Nivel severo de dependencia. Busca ayuda profesional urgentemente.' };
    }
  },
  ludopatia: {
    id: 'ludopatia',
    slug: 'test-ludopatia',
    title: 'Test PGSI: Nivel de Riesgo por Ludopatía',
    shortTitle: 'Test Ludopatía',
    description: 'Evalúa tu adicción al juego/apuestas en 9 preguntas. Resultado: sin problema, bajo, moderado o problemático.',
    icon: Dices,
    color: 'blue',
    duration: '3 minutos',
    questions: [
      { id: 1, text: 'En los últimos 12 meses, ¿ha apostado más de lo que realmente podía permitirse perder?', options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Algunas veces' }, { value: 2, label: 'La mayoría de las veces' }, { value: 3, label: 'Casi siempre' }] },
      { id: 2, text: 'En los últimos 12 meses, ¿ha necesitado apostar con cantidades mayores de dinero para obtener la misma emoción?', options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Algunas veces' }, { value: 2, label: 'La mayoría de las veces' }, { value: 3, label: 'Casi siempre' }] },
      { id: 3, text: 'En los últimos 12 meses, cuando apostó, ¿ha vuelto otro día para intentar recuperar el dinero perdido?', options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Algunas veces' }, { value: 2, label: 'La mayoría de las veces' }, { value: 3, label: 'Casi siempre' }] },
      { id: 4, text: 'En los últimos 12 meses, ¿se ha sentido inquieto o irritable al intentar reducir o parar de apostar?', options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Algunas veces' }, { value: 2, label: 'La mayoría de las veces' }, { value: 3, label: 'Casi siempre' }] },
      { id: 5, text: 'En los últimos 12 meses, ¿ha apostado como una forma de escapar de problemas o de un estado de ánimo bajo?', options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Algunas veces' }, { value: 2, label: 'La mayoría de las veces' }, { value: 3, label: 'Casi siempre' }] },
      { id: 6, text: 'En los últimos 12 meses, después de perder dinero apostando, ¿ha vuelto a apostar para intentar recuperarlo?', options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Algunas veces' }, { value: 2, label: 'La mayoría de las veces' }, { value: 3, label: 'Casi siempre' }] },
      { id: 7, text: 'En los últimos 12 meses, ¿ha mentido a familiares o amigos para ocultar cuánto apostaba?', options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Algunas veces' }, { value: 2, label: 'La mayoría de las veces' }, { value: 3, label: 'Casi siempre' }] },
      { id: 8, text: 'En los últimos 12 meses, ¿ha puesto en riesgo o perdido una relación importante, trabajo o oportunidad educativa por apostar?', options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Algunas veces' }, { value: 2, label: 'La mayoría de las veces' }, { value: 3, label: 'Casi siempre' }] },
      { id: 9, text: 'En los últimos 12 meses, ¿ha pedido dinero prestado o usado dinero destinado a otras cosas para apostar o cubrir deudas de juego?', options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Algunas veces' }, { value: 2, label: 'La mayoría de las veces' }, { value: 3, label: 'Casi siempre' }] }
    ],
    getResult: (score) => {
      if (score === 0) return { level: 'ninguno', title: 'Sin Problema', color: 'emerald', description: 'No se detectan problemas con el juego.' };
      if (score <= 2) return { level: 'bajo', title: 'Riesgo Bajo', color: 'yellow', description: 'Bajo nivel de problemas con el juego. Mantente alerta.' };
      if (score <= 7) return { level: 'moderado', title: 'Riesgo Moderado', color: 'orange', description: 'Riesgo moderado de ludopatía. Considera buscar orientación.' };
      return { level: 'problemático', title: 'Jugador Problemático', color: 'red', description: 'Los resultados indican un problema serio con el juego. Busca ayuda profesional.' };
    }
  },
  urica: {
    id: 'urica',
    slug: 'test-urica',
    title: 'Test URICA: Evalúa tu Estadio de Cambio',
    shortTitle: 'Test URICA',
    description: 'Descubre en qué etapa estás para superar una adicción (precontemplación, contemplación, preparación, acción o mantenimiento).',
    icon: Brain,
    color: 'cyan',
    duration: '5-10 minutos',
    instructions: 'Lee cada frase pensando en tu problema (ej. consumo de drogas/alcohol). Responde cómo te sientes AHORA. Escala: 1 = Muy en desacuerdo → 5 = Muy de acuerdo',
    questions: [
      { id: 1, text: 'En lo que respecta a mí, yo no tengo problemas que necesiten cambio.', stage: 'PC' },
      { id: 2, text: 'Pienso que pueda estar listo(a) para superarme.', stage: 'C' },
      { id: 3, text: 'Estoy haciendo algo sobre los problemas que me estaban molestando.', stage: 'A' },
      { id: 4, text: 'Puede que valga la pena trabajar en mi problema.', stage: 'C' },
      { id: 5, text: 'Yo no soy el/la del problema. No tiene mucho sentido que yo esté aquí.', stage: 'PC' },
      { id: 6, text: 'Me preocupa que caiga de nuevo en un problema que ya he cambiado, por eso estoy aquí para buscar ayuda.', stage: 'M' },
      { id: 7, text: 'Finalmente estoy trabajando en mi problema.', stage: 'A' },
      { id: 8, text: 'He estado pensando que a lo mejor quisiera cambiar algo de mí mismo(a).', stage: 'C' },
      { id: 9, text: 'He tenido éxito trabajando en mi problema pero no estoy seguro(a) que pueda mantener el esfuerzo por mi cuenta.', stage: 'M' },
      { id: 10, text: 'A veces mi problema es difícil, pero estoy trabajando en él.', stage: 'A' },
      { id: 11, text: 'El estar aquí es en sí una pérdida de tiempo para mí porque el problema no tiene que ver mucho conmigo.', stage: 'PC' },
      { id: 12, text: 'Espero que este lugar me ayude a entenderme mejor a mí mismo(a).', stage: 'C' },
      { id: 13, text: 'Puede que tenga defectos, pero no hay nada que realmente necesite cambiar.', stage: 'PC' },
      { id: 14, text: 'Realmente estoy trabajando duro en cambiar.', stage: 'A' },
      { id: 15, text: 'Tengo un problema y realmente pienso que debo trabajar en él.', stage: 'C' },
      { id: 16, text: 'No estoy siguiendo lo que ya había cambiado tan bien como esperaba, y estoy aquí para prevenir caer de nuevo en el problema.', stage: 'M' },
      { id: 17, text: 'Aunque no siempre tengo éxito en cambiar, por lo menos estoy trabajando en el problema.', stage: 'A' },
      { id: 18, text: 'Yo pensé que una vez que hubiese resuelto el problema estaría libre de él, pero a veces todavía me encuentro luchando con él.', stage: 'M' },
      { id: 19, text: 'Desearía tener más ideas sobre cómo resolver mi problema.', stage: 'C' },
      { id: 20, text: 'Empecé a trabajar en mi problema pero me gustaría tener ayuda.', stage: 'A' },
      { id: 21, text: 'A lo mejor este lugar me podrá ayudar.', stage: 'C' },
      { id: 22, text: 'Puede que necesite impulso ahora para que me ayude a mantener los cambios que ya he hecho.', stage: 'M' },
      { id: 23, text: 'Puede que yo sea parte del problema, pero realmente no pienso que lo soy.', stage: 'PC' },
      { id: 24, text: 'Espero que alguien aquí tenga buenos consejos para mí.', stage: 'C' },
      { id: 25, text: 'Cualquiera puede hablar de cambiar; yo realmente estoy haciendo algo sobre el asunto.', stage: 'A' },
      { id: 26, text: 'Toda esta palabrería de psicología es aburrida, ¿por qué la gente simplemente no se olvida de sus problemas?', stage: 'PC' },
      { id: 27, text: 'Yo estoy aquí para prevenir recaer en mi problema.', stage: 'M' },
      { id: 28, text: 'Es frustrante, pero siento que pueda estar recayendo en un problema que pensaba había resuelto.', stage: 'M' },
      { id: 29, text: 'Yo tengo preocupaciones pero también las tiene cualquier otro. ¿Por qué gastar tiempo pensando en ellas?', stage: 'PC' },
      { id: 30, text: 'Yo estoy activamente trabajando en mi problema.', stage: 'A' },
      { id: 31, text: 'Yo preferiría lidiar con mis defectos que tratar de cambiarlos.', stage: 'PC' },
      { id: 32, text: 'Después de todo lo que había hecho para tratar de cambiar mis problemas, de vez en cuando vuelven a rondarme.', stage: 'M' }
    ].map(q => ({
      ...q,
      options: [
        { value: 1, label: '1 - Muy en desacuerdo' },
        { value: 2, label: '2' },
        { value: 3, label: '3 - Indeciso' },
        { value: 4, label: '4' },
        { value: 5, label: '5 - Muy de acuerdo' }
      ]
    })),
    getResult: (answers) => {
      // Calculate scores by stage
      const stages = { PC: [], C: [], A: [], M: [] };
      const test = TESTS.urica;
      test.questions.forEach((q, idx) => {
        if (answers[idx] !== undefined) {
          stages[q.stage].push(answers[idx]);
        }
      });
      
      const avgPC = stages.PC.reduce((a, b) => a + b, 0) / stages.PC.length || 0;
      const avgC = stages.C.reduce((a, b) => a + b, 0) / stages.C.length || 0;
      const avgA = stages.A.reduce((a, b) => a + b, 0) / stages.A.length || 0;
      const avgM = stages.M.reduce((a, b) => a + b, 0) / stages.M.length || 0;
      
      // Readiness score = C + A + M - PC
      const readiness = avgC + avgA + avgM - avgPC;
      
      // Determine dominant stage
      const scores = [
        { stage: 'Precontemplación', avg: avgPC, color: 'red', description: 'Aún no reconoces que hay un problema. El primer paso es tomar conciencia de la situación.' },
        { stage: 'Contemplación', avg: avgC, color: 'orange', description: 'Estás pensando en cambiar. Evalúa los pros y contras de modificar tu comportamiento.' },
        { stage: 'Preparación', avg: (avgC + avgA) / 2, color: 'yellow', description: 'Estás listo para actuar. Planifica los pasos concretos para el cambio.' },
        { stage: 'Acción', avg: avgA, color: 'emerald', description: 'Estás trabajando activamente en el cambio. ¡Sigue adelante!' },
        { stage: 'Mantenimiento', avg: avgM, color: 'cyan', description: 'Estás manteniendo los cambios logrados. Previene recaídas.' }
      ];
      
      const dominant = scores.reduce((a, b) => a.avg > b.avg ? a : b);
      
      return {
        level: dominant.stage.toLowerCase(),
        title: dominant.stage,
        color: dominant.color,
        description: dominant.description,
        readinessScore: readiness.toFixed(1),
        stageScores: { PC: avgPC.toFixed(1), C: avgC.toFixed(1), A: avgA.toFixed(1), M: avgM.toFixed(1) }
      };
    }
  },
  familiar: {
    id: 'familiar',
    slug: 'test-familiar',
    title: 'Test de Riesgo Familiar: ¿Tu Ser Querido Tiene un Problema?',
    shortTitle: 'Test Riesgo Familiar',
    description: 'Evalúa si un familiar o amigo cercano muestra señales de adicción. 15 preguntas sobre cambios de comportamiento y señales de alerta.',
    icon: Users,
    color: 'rose',
    duration: '4 minutos',
    instructions: 'Responde pensando en el comportamiento de tu ser querido durante los últimos 6 meses. Sé lo más honesto posible.',
    questions: [
      {
        id: 1,
        text: '¿Has notado cambios significativos en su personalidad o estado de ánimo (más irritable, agresivo o deprimido)?',
        options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Rara vez' }, { value: 2, label: 'A veces' }, { value: 3, label: 'Frecuentemente' }]
      },
      {
        id: 2,
        text: '¿Se ha alejado de familiares y amigos cercanos, prefiriendo estar solo o con nuevas compañías?',
        options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Rara vez' }, { value: 2, label: 'A veces' }, { value: 3, label: 'Frecuentemente' }]
      },
      {
        id: 3,
        text: '¿Ha descuidado sus responsabilidades en el trabajo, estudios o el hogar?',
        options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Rara vez' }, { value: 2, label: 'A veces' }, { value: 3, label: 'Frecuentemente' }]
      },
      {
        id: 4,
        text: '¿Has notado que le falta dinero sin explicación o que pide prestado con frecuencia?',
        options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Rara vez' }, { value: 2, label: 'A veces' }, { value: 3, label: 'Frecuentemente' }]
      },
      {
        id: 5,
        text: '¿Ha mentido sobre dónde estaba, con quién estaba o qué estaba haciendo?',
        options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Rara vez' }, { value: 2, label: 'A veces' }, { value: 3, label: 'Frecuentemente' }]
      },
      {
        id: 6,
        text: '¿Has encontrado evidencia de consumo de sustancias (botellas vacías, pastillas, papelillos, etc.)?',
        options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Una vez' }, { value: 2, label: 'Algunas veces' }, { value: 3, label: 'Frecuentemente' }]
      },
      {
        id: 7,
        text: '¿Ha tenido problemas legales relacionados con alcohol o drogas (accidentes, detenciones, multas)?',
        options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Una vez' }, { value: 2, label: 'Algunas veces' }, { value: 3, label: 'Frecuentemente' }]
      },
      {
        id: 8,
        text: '¿Has notado cambios en su apariencia física (pérdida de peso, ojeras, descuido personal)?',
        options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Rara vez' }, { value: 2, label: 'A veces' }, { value: 3, label: 'Frecuentemente' }]
      },
      {
        id: 9,
        text: '¿Tiene cambios notables en sus patrones de sueño (duerme mucho o muy poco)?',
        options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Rara vez' }, { value: 2, label: 'A veces' }, { value: 3, label: 'Frecuentemente' }]
      },
      {
        id: 10,
        text: '¿Se pone a la defensiva o agresivo cuando le preguntas sobre su consumo de alcohol o drogas?',
        options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Rara vez' }, { value: 2, label: 'A veces' }, { value: 3, label: 'Frecuentemente' }]
      },
      {
        id: 11,
        text: '¿Ha prometido dejar de consumir o reducir su consumo pero no lo ha logrado?',
        options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Una vez' }, { value: 2, label: 'Algunas veces' }, { value: 3, label: 'Muchas veces' }]
      },
      {
        id: 12,
        text: '¿Has notado que necesita consumir más cantidad para lograr el mismo efecto que antes?',
        options: [{ value: 0, label: 'No he notado' }, { value: 1, label: 'Posiblemente' }, { value: 2, label: 'Probablemente sí' }, { value: 3, label: 'Definitivamente sí' }]
      },
      {
        id: 13,
        text: '¿Ha dejado de hacer actividades que antes disfrutaba (deportes, hobbies, reuniones familiares)?',
        options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Algunas' }, { value: 2, label: 'Varias' }, { value: 3, label: 'Casi todas' }]
      },
      {
        id: 14,
        text: '¿Ha tenido síntomas físicos cuando no consume (temblores, sudoración, náuseas, ansiedad)?',
        options: [{ value: 0, label: 'Nunca' }, { value: 1, label: 'Rara vez' }, { value: 2, label: 'A veces' }, { value: 3, label: 'Frecuentemente' }]
      },
      {
        id: 15,
        text: '¿El consumo de tu ser querido ha afectado negativamente tu vida y la de tu familia?',
        options: [{ value: 0, label: 'Nada' }, { value: 1, label: 'Un poco' }, { value: 2, label: 'Bastante' }, { value: 3, label: 'Mucho' }]
      }
    ],
    getResult: (score) => {
      if (score <= 8) return { 
        level: 'bajo', 
        title: 'Riesgo Bajo', 
        color: 'emerald', 
        description: 'Las señales observadas son mínimas. Sin embargo, mantén la comunicación abierta y estate atento a cambios de comportamiento.' 
      };
      if (score <= 18) return { 
        level: 'moderado', 
        title: 'Riesgo Moderado', 
        color: 'yellow', 
        description: 'Hay algunas señales de alerta. Es recomendable conversar con tu ser querido de manera comprensiva y considerar buscar orientación profesional.' 
      };
      if (score <= 28) return { 
        level: 'alto', 
        title: 'Riesgo Alto', 
        color: 'orange', 
        description: 'Las señales indican un posible problema serio. Es importante buscar ayuda profesional y considerar una intervención familiar.' 
      };
      return { 
        level: 'muy-alto', 
        title: 'Riesgo Muy Alto', 
        color: 'red', 
        description: 'Los indicadores sugieren un problema grave que requiere atención urgente. Busca ayuda profesional inmediatamente. No enfrentes esto solo/a.' 
      };
    }
  },
  codependencia: {
    id: 'codependencia',
    slug: 'test-codependencia',
    title: 'Test de Codependencia: ¿Tu Forma de Ayudar Te Está Afectando?',
    shortTitle: 'Test Codependencia',
    description: 'Evalúa si tienes patrones de codependencia emocional. Basado en la Escala Spann-Fischer. 16 preguntas sobre tus relaciones y comportamientos.',
    icon: Heart,
    color: 'pink',
    duration: '4 minutos',
    instructions: 'Responde pensando en cómo te comportas habitualmente en tus relaciones cercanas (pareja, familia, amigos). Sé honesto/a contigo mismo/a.',
    questions: [
      {
        id: 1,
        text: 'Me cuesta mucho tomar decisiones por mí mismo/a.',
        options: [{ value: 1, label: 'Totalmente en desacuerdo' }, { value: 2, label: 'En desacuerdo' }, { value: 3, label: 'Algo en desacuerdo' }, { value: 4, label: 'Algo de acuerdo' }, { value: 5, label: 'De acuerdo' }, { value: 6, label: 'Totalmente de acuerdo' }]
      },
      {
        id: 2,
        text: 'Me cuesta decir "no" a los demás, aunque quiera hacerlo.',
        options: [{ value: 1, label: 'Totalmente en desacuerdo' }, { value: 2, label: 'En desacuerdo' }, { value: 3, label: 'Algo en desacuerdo' }, { value: 4, label: 'Algo de acuerdo' }, { value: 5, label: 'De acuerdo' }, { value: 6, label: 'Totalmente de acuerdo' }]
      },
      {
        id: 3,
        text: 'Me cuesta aceptar cumplidos o reconocimientos como algo merecido.',
        options: [{ value: 1, label: 'Totalmente en desacuerdo' }, { value: 2, label: 'En desacuerdo' }, { value: 3, label: 'Algo en desacuerdo' }, { value: 4, label: 'Algo de acuerdo' }, { value: 5, label: 'De acuerdo' }, { value: 6, label: 'Totalmente de acuerdo' }]
      },
      {
        id: 4,
        text: 'A veces casi me aburro o me siento vacío/a si no hay problemas que resolver.',
        options: [{ value: 1, label: 'Totalmente en desacuerdo' }, { value: 2, label: 'En desacuerdo' }, { value: 3, label: 'Algo en desacuerdo' }, { value: 4, label: 'Algo de acuerdo' }, { value: 5, label: 'De acuerdo' }, { value: 6, label: 'Totalmente de acuerdo' }]
      },
      {
        id: 5,
        text: 'Valgo tanto como lo que hago por los demás.',
        options: [{ value: 1, label: 'Totalmente en desacuerdo' }, { value: 2, label: 'En desacuerdo' }, { value: 3, label: 'Algo en desacuerdo' }, { value: 4, label: 'Algo de acuerdo' }, { value: 5, label: 'De acuerdo' }, { value: 6, label: 'Totalmente de acuerdo' }]
      },
      {
        id: 6,
        text: 'Me siento responsable de los sentimientos y problemas de los demás.',
        options: [{ value: 1, label: 'Totalmente en desacuerdo' }, { value: 2, label: 'En desacuerdo' }, { value: 3, label: 'Algo en desacuerdo' }, { value: 4, label: 'Algo de acuerdo' }, { value: 5, label: 'De acuerdo' }, { value: 6, label: 'Totalmente de acuerdo' }]
      },
      {
        id: 7,
        text: 'Creo que todo me irá mejor cuando las personas cercanas a mí cambien.',
        options: [{ value: 1, label: 'Totalmente en desacuerdo' }, { value: 2, label: 'En desacuerdo' }, { value: 3, label: 'Algo en desacuerdo' }, { value: 4, label: 'Algo de acuerdo' }, { value: 5, label: 'De acuerdo' }, { value: 6, label: 'Totalmente de acuerdo' }]
      },
      {
        id: 8,
        text: 'Parece que en mis relaciones siempre doy más de lo que recibo.',
        options: [{ value: 1, label: 'Totalmente en desacuerdo' }, { value: 2, label: 'En desacuerdo' }, { value: 3, label: 'Algo en desacuerdo' }, { value: 4, label: 'Algo de acuerdo' }, { value: 5, label: 'De acuerdo' }, { value: 6, label: 'Totalmente de acuerdo' }]
      },
      {
        id: 9,
        text: 'A veces me concentro tanto en otra persona que descuido mis propias responsabilidades.',
        options: [{ value: 1, label: 'Totalmente en desacuerdo' }, { value: 2, label: 'En desacuerdo' }, { value: 3, label: 'Algo en desacuerdo' }, { value: 4, label: 'Algo de acuerdo' }, { value: 5, label: 'De acuerdo' }, { value: 6, label: 'Totalmente de acuerdo' }]
      },
      {
        id: 10,
        text: 'A menudo me veo involucrado/a en relaciones que me hacen daño.',
        options: [{ value: 1, label: 'Totalmente en desacuerdo' }, { value: 2, label: 'En desacuerdo' }, { value: 3, label: 'Algo en desacuerdo' }, { value: 4, label: 'Algo de acuerdo' }, { value: 5, label: 'De acuerdo' }, { value: 6, label: 'Totalmente de acuerdo' }]
      },
      {
        id: 11,
        text: 'Me alejo de mis amistades y familiares por dedicarme a una persona específica.',
        options: [{ value: 1, label: 'Totalmente en desacuerdo' }, { value: 2, label: 'En desacuerdo' }, { value: 3, label: 'Algo en desacuerdo' }, { value: 4, label: 'Algo de acuerdo' }, { value: 5, label: 'De acuerdo' }, { value: 6, label: 'Totalmente de acuerdo' }]
      },
      {
        id: 12,
        text: 'Siento ansiedad o miedo intenso ante la idea de ser abandonado/a.',
        options: [{ value: 1, label: 'Totalmente en desacuerdo' }, { value: 2, label: 'En desacuerdo' }, { value: 3, label: 'Algo en desacuerdo' }, { value: 4, label: 'Algo de acuerdo' }, { value: 5, label: 'De acuerdo' }, { value: 6, label: 'Totalmente de acuerdo' }]
      },
      {
        id: 13,
        text: 'Cuando alguien cercano tiene un problema, lo vivo como si fuera mío.',
        options: [{ value: 1, label: 'Totalmente en desacuerdo' }, { value: 2, label: 'En desacuerdo' }, { value: 3, label: 'Algo en desacuerdo' }, { value: 4, label: 'Algo de acuerdo' }, { value: 5, label: 'De acuerdo' }, { value: 6, label: 'Totalmente de acuerdo' }]
      },
      {
        id: 14,
        text: 'Tiendo a minimizar o negar mis propios sentimientos y necesidades.',
        options: [{ value: 1, label: 'Totalmente en desacuerdo' }, { value: 2, label: 'En desacuerdo' }, { value: 3, label: 'Algo en desacuerdo' }, { value: 4, label: 'Algo de acuerdo' }, { value: 5, label: 'De acuerdo' }, { value: 6, label: 'Totalmente de acuerdo' }]
      },
      {
        id: 15,
        text: 'Frecuentemente intento controlar o "arreglar" a las personas cercanas.',
        options: [{ value: 1, label: 'Totalmente en desacuerdo' }, { value: 2, label: 'En desacuerdo' }, { value: 3, label: 'Algo en desacuerdo' }, { value: 4, label: 'Algo de acuerdo' }, { value: 5, label: 'De acuerdo' }, { value: 6, label: 'Totalmente de acuerdo' }]
      },
      {
        id: 16,
        text: 'A menudo pongo las necesidades de otras personas por encima de las mías.',
        options: [{ value: 1, label: 'Totalmente en desacuerdo' }, { value: 2, label: 'En desacuerdo' }, { value: 3, label: 'Algo en desacuerdo' }, { value: 4, label: 'Algo de acuerdo' }, { value: 5, label: 'De acuerdo' }, { value: 6, label: 'Totalmente de acuerdo' }]
      }
    ],
    getResult: (score) => {
      // Escala 16-96 puntos
      if (score <= 32) return { 
        level: 'bajo', 
        title: 'Nivel Bajo de Codependencia', 
        color: 'emerald', 
        description: 'Tus patrones relacionales parecen saludables. Mantienes un buen equilibrio entre cuidar a otros y cuidarte a ti mismo/a.' 
      };
      if (score <= 48) return { 
        level: 'leve', 
        title: 'Tendencia Leve', 
        color: 'yellow', 
        description: 'Muestras algunas tendencias codependientes leves. Es bueno que estés consciente de esto. Trabaja en establecer límites saludables.' 
      };
      if (score <= 64) return { 
        level: 'moderado', 
        title: 'Nivel Moderado', 
        color: 'orange', 
        description: 'Presentas patrones codependientes moderados que pueden estar afectando tu bienestar. Considera buscar apoyo profesional o grupos de ayuda.' 
      };
      if (score <= 80) return { 
        level: 'alto', 
        title: 'Nivel Alto', 
        color: 'red', 
        description: 'Tus patrones de codependencia son significativos. Es importante que busques ayuda profesional para trabajar en tu bienestar emocional.' 
      };
      return { 
        level: 'muy-alto', 
        title: 'Nivel Muy Alto', 
        color: 'red', 
        description: 'Los resultados indican patrones de codependencia severos. Te recomendamos buscar ayuda profesional urgentemente. Mereces cuidarte a ti mismo/a.' 
      };
    }
  }
};

// ==================== TESTS LANDING PAGE ====================

export function TestsLanding() {
  // Tests evaluativos
  const tests = [
    { ...TESTS.alcoholismo, href: '/herramientas/test-alcoholismo' },
    { ...TESTS.dependencia, href: '/herramientas/test-dependencia' },
    { ...TESTS.ludopatia, href: '/herramientas/test-ludopatia' },
    { ...TESTS.urica, href: '/herramientas/test-urica' },
    { ...TESTS.familiar, href: '/herramientas/test-familiar' },
    { ...TESTS.codependencia, href: '/herramientas/test-codependencia' },
  ];

  // Experiencias inmersivas
  const experiencias = [
    {
      id: 'nelson',
      title: 'La Última Llamada de Nelson',
      description: 'Una experiencia inmersiva basada en historia real. Atiende a Nelson, una persona en crisis, en sus últimos minutos. Cinematográfica, con audio y video.',
      icon: Phone,
      color: 'rose',
      duration: '5 minutos',
      href: '/experiencia',
      badge: 'Crisis · Historia real',
    },
    {
      id: 'yo-futuro',
      title: 'Tu Yo del Futuro',
      description: 'Conversa con la versión de ti mismo en 1, 5 o 10 años. Te confronta con honestidad sobre la decisión que estás tomando hoy con tu consumo.',
      icon: UserCircle,
      color: 'cyan',
      duration: '4 minutos',
      href: '/yo-del-futuro',
      badge: 'Conversación con IA',
    },
    {
      id: 'espejo',
      title: 'El Espejo del Tiempo',
      description: 'Sube una selfie y mira cómo te verás en 10 años si continúas el consumo. Imagen generada con IA. No guardamos tu foto.',
      icon: Eye,
      color: 'purple',
      duration: '2 minutos',
      href: '/espejo',
      badge: 'IA · 100% privado',
    },
  ];

  const toolsSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "¿Cómo saber si tengo problemas con el alcohol?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Puedes usar el Test AUDIT-10, validado por la OMS, para evaluar tu nivel de riesgo con el consumo de alcohol. Es gratuito y confidencial."
        }
      },
      {
        "@type": "Question",
        "name": "¿Qué es el Reto 21 Días sin Drogas?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Es un programa gratuito de 21 días con plan PDF personalizado y emails diarios de acompañamiento. Basado en neurociencia y modelos clínicos validados como Marlatt."
        }
      },
      {
        "@type": "Question",
        "name": "¿Cómo ayudar a un familiar con adicción?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Puedes usar el Test de Riesgo Familiar para evaluar señales de alerta en tu ser querido, y el Test de Codependencia para evaluar cómo te está afectando la situación."
        }
      }
    ]
  };

  const colorClasses = {
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    cyan: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    rose: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
    pink: 'bg-pink-500/10 text-pink-400 border-pink-500/30',
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <title>Herramientas Gratis: Reto 21 Días, Tests, Cotizador y Experiencias | SinAdicciones.org</title>
      <meta name="description" content="Todas las herramientas de SinAdicciones.org en un solo lugar: Reto 21 Días con plan personalizado, cotizador de centros, calculadora de costos, 6 tests de adicciones validados y 3 experiencias inmersivas. 100% gratis." />
      <meta name="keywords" content="reto 21 días, test alcoholismo, test drogas, cotizar tratamiento, calculadora costos, experiencias adicciones, AUDIT, DAST" />
      <meta property="og:title" content="Herramientas Gratis para Vencer una Adicción | SinAdicciones.org" />
      <meta property="og:description" content="Reto 21 Días, tests validados OMS/SENDA, cotizador, calculadora y experiencias inmersivas. Todo gratis y confidencial." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://sinadicciones.org/herramientas" />
      <meta property="og:site_name" content="SinAdicciones.org" />
      <meta property="og:locale" content="es_CL" />
      <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
      <meta property="og:image:width" content="1280" />
      <meta property="og:image:height" content="896" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
      <link rel="canonical" href="https://sinadicciones.org/herramientas" />
      <script type="application/ld+json">{JSON.stringify(toolsSchema)}</script>

      <Header />
      <main className="pt-24 pb-16">
        {/* Hero */}
        <section className="bg-slate-900 border-b border-slate-800 py-14">
          <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/10 mb-5">
              <ClipboardCheck className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-50 mb-4" data-testid="tools-title">
              Todas nuestras herramientas en un solo lugar
            </h1>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              Reto 21 Días con plan personalizado, cotizador de centros, calculadora de costos, 
              tests de adicciones validados y experiencias inmersivas. <span className="text-emerald-400 font-semibold">100% gratis y confidencial.</span>
            </p>
          </div>
        </section>

        {/* HERO: Reto 21 Días (Destacado) */}
        <section className="py-12 md:py-16 px-4 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full mb-4">
              <Trophy className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs uppercase tracking-[0.2em] text-emerald-300 font-mono">Lo más usado</span>
            </div>
            <Link to="/reto-21-dias" data-testid="tool-reto21" className="block group">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600/15 via-slate-900 to-slate-900 border border-emerald-500/30 hover:border-emerald-400/60 transition-all duration-500">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_50%,rgba(16,185,129,0.12),transparent_55%)] pointer-events-none" />
                <div className="relative grid md:grid-cols-5 gap-6 p-7 md:p-10">
                  <div className="md:col-span-3">
                    <div className="flex items-center gap-2 mb-4">
                      <Flame className="w-5 h-5 text-emerald-400" />
                      <span className="text-xs uppercase tracking-[0.2em] text-emerald-300 font-mono">Programa estrella · 21 días · PDF + Emails</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                      Reto de <span className="text-emerald-400">21 días sin drogas</span>
                    </h2>
                    <p className="text-base text-slate-300 mb-5 leading-relaxed">
                      Un plan personalizado en PDF de 27 páginas con tu historia, gatillos, hábitos y el nuevo <strong className="text-white">Mapa Inverso</strong>. 
                      Más 21 emails diarios de acompañamiento. Basado en neurociencia y modelo Marlatt.
                    </p>
                    <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-400 mb-5">
                      <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> 5 min para crearlo</span>
                      <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> Gratis, sin tarjeta</span>
                      <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> 100% confidencial</span>
                    </div>
                    <span className="inline-flex items-center gap-2 px-5 py-3 bg-emerald-500 text-slate-950 font-bold rounded-lg group-hover:bg-emerald-400 transition-all shadow-lg shadow-emerald-500/20">
                      Empezar mi Reto 21 Días
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                  <div className="md:col-span-2 flex md:items-center md:justify-center">
                    <div className="bg-slate-950/60 backdrop-blur-sm border border-slate-700/50 rounded-xl p-5 w-full">
                      <p className="text-emerald-300 text-[10px] uppercase tracking-[0.3em] font-mono mb-3">El dato</p>
                      <p className="text-5xl font-light text-white mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                        3<span className="text-emerald-400">×</span>
                      </p>
                      <p className="text-sm text-slate-400 leading-relaxed">
                        Tu probabilidad de recuperación a largo plazo se <span className="text-white font-semibold">triplica</span> si completas los 21 días, según estudios NIDA.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* Destacado #2: Cotizador */}
        <section className="px-4 md:px-8 mb-12">
          <div className="max-w-6xl mx-auto">
            <Link to="/cotizar" data-testid="tool-cotizar" className="block group">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-600/10 via-slate-900 to-slate-900 border border-amber-500/30 hover:border-amber-400/60 transition-all duration-500 p-7 md:p-9">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_50%,rgba(245,158,11,0.08),transparent_55%)] pointer-events-none" />
                <div className="relative grid md:grid-cols-12 gap-6 items-center">
                  <div className="md:col-span-2 flex md:justify-center">
                    <div className="w-16 h-16 rounded-2xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
                      <MessageSquare className="w-8 h-8 text-amber-400" />
                    </div>
                  </div>
                  <div className="md:col-span-7">
                    <p className="text-amber-300 text-xs uppercase tracking-[0.2em] font-mono mb-2">Recomendado · 2 min</p>
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                      Recibe cotizaciones de los <span className="text-amber-400">mejores centros</span>
                    </h2>
                    <p className="text-sm md:text-base text-slate-400 leading-relaxed">
                      Cuéntanos qué necesitas y conectamos tu caso con 2-3 centros que matchean por modalidad, región y especialidad. 
                      Respuesta en menos de 24 horas.
                    </p>
                  </div>
                  <div className="md:col-span-3 md:text-right">
                    <span className="inline-flex items-center gap-2 px-5 py-3 bg-amber-500 text-slate-950 font-bold rounded-lg group-hover:bg-amber-400 transition-all">
                      Cotizar gratis
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* Calculadora */}
        <section className="px-4 md:px-8 mb-14">
          <div className="max-w-6xl mx-auto">
            <Link to="/calculadora" data-testid="tool-calculadora" className="block group">
              <Card className="bg-slate-900 border-slate-800 hover:border-emerald-500/40 transition-all duration-300">
                <CardContent className="p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center gap-5">
                  <div className="w-14 h-14 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                    <Calculator className="w-7 h-7" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-slate-50 mb-1 group-hover:text-emerald-400 transition-colors">Calculadora de Costos</h3>
                    <p className="text-sm text-slate-400">
                      Estima el costo real de tu tratamiento en Chile según modalidad, duración y dependencia. <span className="text-emerald-400">~2 minutos.</span>
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all" />
                </CardContent>
              </Card>
            </Link>
          </div>
        </section>

        {/* TerapeutIA — Chat IA especializado */}
        <section className="px-4 md:px-8 mb-14">
          <div className="max-w-6xl mx-auto">
            <Link to="/terapeutia" data-testid="tool-terapeutia" className="block group">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600/15 via-slate-900 to-slate-900 border border-teal-500/30 hover:border-teal-400/60 transition-all duration-500">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_50%,rgba(20,184,166,0.12),transparent_55%)] pointer-events-none" />
                <div className="relative grid md:grid-cols-5 gap-6 p-7 md:p-10 items-center">
                  <div className="md:col-span-3">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-5 h-5 text-teal-400" />
                      <span className="text-xs uppercase tracking-[0.2em] text-teal-300 font-mono">Nuevo · IA · Claude Sonnet 4.5</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                      <span className="text-teal-400">TerapeutIA</span> · chat especializado en adicciones
                    </h2>
                    <p className="text-base text-slate-300 mb-5 leading-relaxed">
                      Conversa con una IA enfocada <strong className="text-white">solo en adicciones, tratamientos y rehabilitación</strong>. Respuestas con fuentes (SENDA, OMS, NIDA, APA, estudio Reto 21). No reemplaza terapeuta, no entrega diagnóstico.
                    </p>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="px-2 py-1 rounded bg-teal-500/10 border border-teal-500/30 text-teal-300">Gratis</span>
                      <span className="px-2 py-1 rounded bg-teal-500/10 border border-teal-500/30 text-teal-300">Confidencial</span>
                      <span className="px-2 py-1 rounded bg-teal-500/10 border border-teal-500/30 text-teal-300">Foco clínico</span>
                    </div>
                  </div>
                  <div className="md:col-span-2 flex justify-center md:justify-end">
                    <div className="inline-flex items-center px-5 py-3 rounded-lg bg-teal-500 hover:bg-teal-600 text-white font-semibold transition-colors group-hover:translate-x-1">
                      Hablar con TerapeutIA <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* Experiencias inmersivas */}
        <section className="px-4 md:px-8 mb-14">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <h2 className="text-2xl md:text-3xl font-bold text-slate-50">Experiencias inmersivas</h2>
            </div>
            <p className="text-slate-400 mb-7 max-w-3xl">
              Tres experiencias cinematográficas diseñadas para confrontar y motivar el cambio. Pensadas para vivirlas, no solo leerlas.
            </p>
            <div className="grid md:grid-cols-3 gap-5">
              {experiencias.map(exp => {
                const Icon = exp.icon;
                return (
                  <Link key={exp.id} to={exp.href} data-testid={`tool-experiencia-${exp.id}`}>
                    <Card className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-all duration-300 h-full group cursor-pointer">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className={`w-14 h-14 rounded-xl ${colorClasses[exp.color]} border flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                            <Icon className="w-7 h-7" />
                          </div>
                          <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-1 rounded ${colorClasses[exp.color]} border`}>
                            {exp.badge}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-slate-50 mb-2 group-hover:text-emerald-400 transition-colors leading-snug">
                          {exp.title}
                        </h3>
                        <p className="text-sm text-slate-400 mb-4 leading-relaxed">
                          {exp.description}
                        </p>
                        <div className="flex items-center text-xs text-slate-500">
                          <Zap className="w-4 h-4 mr-1" />
                          {exp.duration}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Tests de evaluación */}
        <section className="px-4 md:px-8 mb-14">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <ClipboardCheck className="w-5 h-5 text-emerald-400" />
              <h2 className="text-2xl md:text-3xl font-bold text-slate-50">Tests de evaluación</h2>
            </div>
            <p className="text-slate-400 mb-7 max-w-3xl">
              Tests confidenciales basados en instrumentos validados por <strong className="text-slate-300">OMS y SENDA</strong>. Resultado inmediato sin guardar tus respuestas.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {tests.map(tool => {
                const Icon = tool.icon;
                return (
                  <Link key={tool.id} to={tool.href} data-testid={`tool-${tool.id}`}>
                    <Card className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-all duration-300 h-full group cursor-pointer">
                      <CardContent className="p-6">
                        <div className={`w-14 h-14 rounded-xl ${colorClasses[tool.color]} border flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className="w-7 h-7" />
                        </div>
                        <h3 className="text-base font-semibold text-slate-50 mb-2 group-hover:text-emerald-400 transition-colors">
                          {tool.shortTitle}
                        </h3>
                        <p className="text-sm text-slate-400 mb-4 line-clamp-3 leading-relaxed">
                          {tool.description}
                        </p>
                        <div className="flex items-center text-xs text-slate-500">
                          <ClipboardCheck className="w-4 h-4 mr-1" />
                          {tool.duration}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Info Section */}
        <section className="py-12 border-t border-slate-800">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-slate-50 mb-6">¿Cómo funcionan estas herramientas?</h2>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-semibold text-emerald-400 mb-3">Confidencial y Gratuito</h3>
                    <p className="text-sm text-slate-400">
                      Tus respuestas no se guardan ni se comparten. Los resultados son solo para ti.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-emerald-400 mb-3">Validados Científicamente</h3>
                    <p className="text-sm text-slate-400">
                      Los tests están basados en instrumentos utilizados por OMS, SENDA y profesionales de salud mental.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-emerald-400 mb-3">Orientación Inmediata</h3>
                    <p className="text-sm text-slate-400">
                      Obtén tu resultado al instante con recomendaciones personalizadas según tu nivel de riesgo.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-emerald-400 mb-3">No Reemplaza Diagnóstico</h3>
                    <p className="text-sm text-slate-400">
                      Estos tests son orientativos. Para un diagnóstico profesional, consulta con un especialista o agenda una <Link to="/asesoria" className="text-emerald-400 hover:underline">asesoría experta</Link>.
                    </p>
                  </div>
                </div>
                
                <div className="mt-8 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-yellow-400 font-medium">¿Necesitas ayuda urgente?</p>
                      <p className="text-sm text-slate-400 mt-1">
                        Llama al <strong>Fono Drogas y Alcohol 1412</strong> (24/7 confidencial) o busca un 
                        <Link to="/centros-de-rehabilitacion-drogas-chile" className="text-emerald-400 hover:underline ml-1">centro de rehabilitación</Link>.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

// ==================== INDIVIDUAL TEST PAGE ====================

export function TestPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);

  // Map URL param to test key (test-alcoholismo -> alcoholismo)
  const testKey = testId?.replace('test-', '');
  const test = TESTS[testKey];

  useEffect(() => {
    // Reset state when test changes (no auto-redirect; mostramos 404 abajo si testKey inválido)
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
    setResult(null);
  }, [testKey, test, navigate]);

  // Test no existe → 404 con noindex (evita Soft 404 en GSC)
  if (!test) return <NotFoundPage />;

  const Icon = test.icon;
  const totalQuestions = test.questions.length;
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;
  const currentQ = test.questions[currentQuestion];
  const isLastQuestion = currentQuestion === totalQuestions - 1;
  const canGoNext = answers[currentQuestion] !== undefined;

  function handleAnswer(value) {
    setAnswers(prev => ({ ...prev, [currentQuestion]: value }));
  }

  function goNext() {
    if (isLastQuestion) {
      calculateResult();
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  }

  function goPrev() {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  }

  function calculateResult() {
    if (testKey === 'urica') {
      // URICA has special scoring
      const answersArray = test.questions.map((_, idx) => answers[idx]);
      setResult(test.getResult(answersArray));
    } else {
      // Sum all answers
      const score = Object.values(answers).reduce((a, b) => a + b, 0);
      setResult(test.getResult(score));
    }
    setShowResult(true);
  }

  function restart() {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
    setResult(null);
  }

  const colorClasses = {
    amber: 'bg-amber-500/10 text-amber-400',
    purple: 'bg-purple-500/10 text-purple-400',
    blue: 'bg-blue-500/10 text-blue-400',
    cyan: 'bg-cyan-500/10 text-cyan-400',
    emerald: 'bg-emerald-500/10 text-emerald-400',
    rose: 'bg-rose-500/10 text-rose-400',
    pink: 'bg-pink-500/10 text-pink-400'
  };

  const resultColorClasses = {
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
    yellow: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',
    orange: 'bg-orange-500/10 border-orange-500/30 text-orange-400',
    red: 'bg-red-500/10 border-red-500/30 text-red-400',
    cyan: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400'
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            asChild 
            className="mb-6 text-slate-400 hover:text-emerald-400 hover:bg-transparent"
          >
            <Link to="/herramientas">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a herramientas
            </Link>
          </Button>

          {!showResult ? (
            <>
              {/* Test Header */}
              <div className="text-center mb-8">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl ${colorClasses[test.color]} mb-4`}>
                  <Icon className="w-8 h-8" />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-50 mb-2">{test.title}</h1>
                <p className="text-slate-400">Responde sobre los últimos 12 meses. ¡Confidencial!</p>
                {test.instructions && (
                  <p className="text-sm text-slate-500 mt-2">{test.instructions}</p>
                )}
              </div>

              {/* Progress */}
              <div className="mb-8">
                <div className="flex justify-between text-sm text-slate-400 mb-2">
                  <span>Pregunta {currentQuestion + 1} de {totalQuestions}</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-2 bg-slate-800" />
              </div>

              {/* Question Card */}
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6 md:p-8">
                  <h2 className="text-lg md:text-xl font-medium text-slate-50 mb-6">
                    {currentQ.text}
                  </h2>

                  <RadioGroup 
                    value={answers[currentQuestion]?.toString()} 
                    onValueChange={(v) => handleAnswer(parseInt(v))}
                    className="space-y-3"
                  >
                    {currentQ.options.map((option, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-center p-4 rounded-lg border transition-all cursor-pointer
                          ${answers[currentQuestion] === option.value 
                            ? 'border-emerald-500 bg-emerald-500/10' 
                            : 'border-slate-700 hover:border-slate-600 hover:bg-slate-800/50'}`}
                        onClick={() => handleAnswer(option.value)}
                      >
                        <RadioGroupItem 
                          value={option.value.toString()} 
                          id={`option-${idx}`}
                          className="border-slate-600 text-emerald-500"
                        />
                        <Label 
                          htmlFor={`option-${idx}`} 
                          className="ml-3 cursor-pointer text-slate-300 flex-1"
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>

                  {/* Navigation */}
                  <div className="flex justify-between mt-8">
                    <Button 
                      variant="outline" 
                      onClick={goPrev}
                      disabled={currentQuestion === 0}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Anterior
                    </Button>
                    <Button 
                      onClick={goNext}
                      disabled={!canGoNext}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      {isLastQuestion ? 'Ver Resultado' : 'Siguiente'}
                      {!isLastQuestion && <ArrowRight className="w-4 h-4 ml-2" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* Result */}
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6 md:p-8">
                  <div className="text-center mb-8">
                    <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${resultColorClasses[result.color]} border mb-4`}>
                      {result.color === 'emerald' || result.color === 'cyan' ? (
                        <CheckCircle className="w-10 h-10" />
                      ) : result.color === 'yellow' ? (
                        <AlertCircle className="w-10 h-10" />
                      ) : (
                        <AlertTriangle className="w-10 h-10" />
                      )}
                    </div>
                    <h2 className="text-3xl font-bold text-slate-50 mb-2">
                      {result.title}
                    </h2>
                    <p className="text-slate-400 max-w-lg mx-auto">
                      {result.description}
                    </p>
                  </div>

                  {/* URICA specific: show stage scores */}
                  {testKey === 'urica' && result.stageScores && (
                    <div className="mb-8 p-4 bg-slate-800/50 rounded-lg">
                      <h3 className="font-semibold text-slate-50 mb-3">Puntuaciones por Etapa</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-bold text-red-400">{result.stageScores.PC}</p>
                          <p className="text-xs text-slate-500">Precontemplación</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-orange-400">{result.stageScores.C}</p>
                          <p className="text-xs text-slate-500">Contemplación</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-emerald-400">{result.stageScores.A}</p>
                          <p className="text-xs text-slate-500">Acción</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-cyan-400">{result.stageScores.M}</p>
                          <p className="text-xs text-slate-500">Mantenimiento</p>
                        </div>
                      </div>
                      <p className="text-sm text-slate-400 mt-4 text-center">
                        Índice de Disposición al Cambio: <strong className="text-emerald-400">{result.readinessScore}</strong>
                      </p>
                    </div>
                  )}

                  {/* Recommendations */}
                  <div className="space-y-4 mb-8">
                    <h3 className="font-semibold text-slate-50">¿Qué puedes hacer ahora?</h3>
                    
                    {/* Asesoría Experta - Destacado */}
                    <Link to="/asesoria" className="block">
                      <div className="p-5 bg-gradient-to-r from-amber-500/20 to-emerald-500/10 border border-amber-500/40 rounded-xl hover:border-amber-500/60 transition-colors">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                            <Sparkles className="w-6 h-6 text-amber-400" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-slate-50">Asesoría Experta Personalizada</span>
                              <span className="px-2 py-0.5 bg-amber-500/30 text-amber-400 text-xs font-medium rounded">$35.000</span>
                            </div>
                            <p className="text-sm text-slate-400 mb-2">
                              Un terapeuta con 15 años de experiencia te guía para elegir el tratamiento ideal para tu caso.
                            </p>
                            <span className="text-amber-400 text-sm font-medium">Agendar ahora →</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                    
                    <div className="grid gap-3">
                      <Link to="/centros-de-rehabilitacion-drogas-chile" className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors">
                        <Building2 className="w-5 h-5 text-emerald-400" />
                        <span className="text-slate-300">Buscar centros de rehabilitación</span>
                      </Link>
                      <Link to="/profesionales" className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors">
                        <ClipboardCheck className="w-5 h-5 text-emerald-400" />
                        <span className="text-slate-300">Consultar con un profesional</span>
                      </Link>
                      <Link to="/blog" className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors">
                        <BookOpen className="w-5 h-5 text-emerald-400" />
                        <span className="text-slate-300">Leer artículos sobre recuperación</span>
                      </Link>
                    </div>
                  </div>

                  {/* Help Line */}
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg mb-8">
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-yellow-400" />
                      <div>
                        <p className="text-yellow-400 font-medium">Línea de Ayuda</p>
                        <p className="text-sm text-slate-400">Fono Drogas y Alcohol <strong>1412</strong> (24/7 confidencial)</p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button 
                      variant="outline" 
                      onClick={restart}
                      className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                    >
                      Realizar test de nuevo
                    </Button>
                    <Button 
                      asChild
                      className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                    >
                      <Link to="/herramientas">Ver otros tests</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default TestsLanding;
